import type { AuthResponse, PageMeta, Paged } from "./types";

const BASE = "/api/v1";
const REFRESH_SKEW_MS = 30_000;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string>;
  /** structured details from the server, e.g. the plan limit that was hit */
  readonly metadata: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    message: string,
    fieldErrors: Record<string, string> = {},
    metadata: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.metadata = metadata;
  }
}

interface Envelope<T> {
  ok: boolean;
  data?: T;
  meta_data?: PageMeta;
  error?: {
    /** the HTTP status; some error bodies carry the string code here instead */
    code: number | string;
    error_code?: string;
    message: string;
    metadata?: Record<string, unknown> | null;
    field_error?: { name: string; description: string }[];
  };
}

// ---- session -------------------------------------------------------------
// The access token lives only in memory. The refresh token is an HttpOnly
// cookie scoped to /api/v1/auth that scripts cannot read.

let accessToken: string | null = null;
let accessExpiresAt = 0;
let refreshInFlight: Promise<AuthResponse | null> | null = null;
const sessionListeners = new Set<(session: AuthResponse | null) => void>();

export function setSession(session: AuthResponse | null) {
  accessToken = session?.access_token ?? null;
  accessExpiresAt = session ? new Date(session.access_token_expires_at).getTime() : 0;
  sessionListeners.forEach((l) => l(session));
}

export function onSessionChange(listener: (session: AuthResponse | null) => void) {
  sessionListeners.add(listener);

  return () => sessionListeners.delete(listener);
}

async function doRefresh(): Promise<AuthResponse | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-Token-Transport": "cookie" },
    });

    // 204: the browser has no session cookie (a signed-out visitor).
    if (res.status === 204) return null;

    if (res.ok) {
      const body = (await res.json()) as Envelope<AuthResponse>;

      return body.data ?? null;
    }

    // 409: another tab rotated the token a moment ago; the cookie is fresh now.
    if (res.status === 409 && attempt === 0) {
      await new Promise((r) => setTimeout(r, 250));
      continue;
    }

    return null;
  }

  return null;
}

/** Refreshes the session once, even when many requests or tabs ask at the same time. */
export function refreshSession(): Promise<AuthResponse | null> {
  if (!refreshInFlight) {
    const run = () => doRefresh();
    const locked =
      typeof navigator !== "undefined" && "locks" in navigator
        ? navigator.locks.request("pf-refresh", run)
        : run();

    refreshInFlight = locked
      .then((session) => {
        setSession(session);

        return session;
      })
      .catch(() => {
        setSession(null);

        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

/** A valid access token (refreshed first when it is about to expire), or null when signed out. */
export async function validToken(): Promise<string | null> {
  if (accessToken && Date.now() < accessExpiresAt - REFRESH_SKEW_MS) return accessToken;

  const session = await refreshSession();

  return session?.access_token ?? null;
}

// ---- requests ------------------------------------------------------------

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  body?: unknown;
  form?: FormData;
  query?: Query;
  signal?: AbortSignal;
  /** false for public endpoints */
  auth?: boolean;
  /** send X-Token-Transport (auth endpoints) */
  cookie?: boolean;
}

function buildURL(path: string, query?: Query) {
  const url = new URL(`${BASE}/${path.replace(/^\//, "")}`, window.location.origin);

  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
  }

  return url.toString();
}

async function send<T>(
  method: string,
  path: string,
  opts: RequestOptions,
  retried = false,
): Promise<Envelope<T>> {
  const headers: Record<string, string> = {};
  const auth = opts.auth ?? true;

  if (auth) {
    const token = await validToken();
    if (!token) throw new ApiError(401, "UNAUTHORIZED", "Your session has expired. Please sign in again.");
    headers.Authorization = `Bearer ${token}`;
  }

  if (opts.cookie) headers["X-Token-Transport"] = "cookie";

  let body: BodyInit | undefined;
  if (opts.form) {
    body = opts.form;
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(buildURL(path, opts.query), {
      method,
      headers,
      body,
      signal: opts.signal,
      credentials: "same-origin",
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError(0, "NETWORK", "Could not reach the server. Check your connection.");
  }

  if (res.status === 401 && auth && !retried) {
    const session = await refreshSession();
    if (session) return send<T>(method, path, opts, true);
  }

  if (res.status === 204) return { ok: true };

  let envelope: Envelope<T>;
  try {
    envelope = (await res.json()) as Envelope<T>;
  } catch {
    throw new ApiError(res.status, "BAD_RESPONSE", `Unexpected response (${res.status})`);
  }

  if (!res.ok || !envelope.ok) {
    const e = envelope.error;
    const fields = Object.fromEntries((e?.field_error ?? []).map((f) => [f.name, f.description]));
    const retryAfter = res.headers.get("Retry-After");
    const message =
      res.status === 429 && retryAfter
        ? `Too many requests. Try again in ${retryAfter}s.`
        : (e?.message ?? `Request failed (${res.status})`);

    const code = e?.error_code ?? (typeof e?.code === "string" ? e.code : "ERROR");
    const metadata = e?.metadata && typeof e.metadata === "object" ? e.metadata : {};

    throw new ApiError(res.status, code, message, fields, metadata);
  }

  return envelope;
}

export const api = {
  async get<T>(path: string, opts: RequestOptions = {}) {
    return (await send<T>("GET", path, opts)).data as T;
  },
  async paged<T>(path: string, opts: RequestOptions = {}): Promise<Paged<T>> {
    const env = await send<T[]>("GET", path, opts);

    return { items: env.data ?? [], meta: env.meta_data ?? { page: 1, per_page: 20, total: 0 } };
  },
  async post<T>(path: string, body?: unknown, opts: RequestOptions = {}) {
    return (await send<T>("POST", path, { ...opts, body })).data as T;
  },
  async put<T>(path: string, body?: unknown, opts: RequestOptions = {}) {
    return (await send<T>("PUT", path, { ...opts, body })).data as T;
  },
  async patch<T>(path: string, body?: unknown, opts: RequestOptions = {}) {
    return (await send<T>("PATCH", path, { ...opts, body })).data as T;
  },
  async delete<T = void>(path: string, opts: RequestOptions = {}) {
    return (await send<T>("DELETE", path, opts)).data as T;
  },
  async upload<T>(path: string, form: FormData, opts: RequestOptions = {}) {
    return (await send<T>("POST", path, { ...opts, form })).data as T;
  },
};

const ACRONYMS: Record<string, string> = { url: "URL", id: "ID", api: "API", rss: "RSS", sso: "SSO" };

/** "feed_url" → "Feed URL", for field names in validation errors. */
export function fieldLabel(key: string) {
  const words = key
    .split(/[_.\s]+/)
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.toLowerCase());
  const text = words.join(" ");

  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Server and provider messages read as sentences: first letter upper case. */
function sentence(text: string) {
  const t = text.trim();

  return t.charAt(0).toUpperCase() + t.slice(1);
}

const NETWORK_ERROR = "Can't reach the server. Check your connection and try again.";

export function errorMessage(err: unknown, fallback = "Something went wrong") {
  if (err instanceof ApiError) {
    const fields = Object.entries(err.fieldErrors);
    if (fields.length > 0)
      return `${sentence(err.message)}: ${fields.map(([k, v]) => `${fieldLabel(k)} ${v}`).join(", ")}`;

    return sentence(err.message) || fallback;
  }

  // fetch() rejects with a TypeError when the request never reached the server
  if (err instanceof TypeError && /fetch|network|load failed/i.test(err.message)) return NETWORK_ERROR;

  return err instanceof Error && err.message ? sentence(err.message) : fallback;
}

// ---- plan limits -------------------------------------------------------------

export type PlanLimitKind = "channels" | "members" | "scheduled_posts" | "analytics_days";
export type PlanLimitFeature = "webhooks" | "rss" | "api";

/** Metadata of a 402 PLAN_LIMIT error (see backend/docs/billing.md). */
export interface PlanLimitInfo {
  limit?: PlanLimitKind;
  feature?: PlanLimitFeature;
  plan?: string;
  max?: number;
  used?: number;
}

const LIMIT_KINDS: readonly string[] = ["channels", "members", "scheduled_posts", "analytics_days"];
const LIMIT_FEATURES: readonly string[] = ["webhooks", "rss", "api"];

export function isPlanLimit(err: unknown): err is ApiError {
  return err instanceof ApiError && err.code === "PLAN_LIMIT";
}

/** Typed metadata of a PLAN_LIMIT error, or null for any other error. Unknown values are dropped. */
export function planLimitInfo(err: unknown): PlanLimitInfo | null {
  if (!isPlanLimit(err)) return null;

  const m = err.metadata;
  const info: PlanLimitInfo = {};
  if (typeof m.limit === "string" && LIMIT_KINDS.includes(m.limit)) info.limit = m.limit as PlanLimitKind;
  if (typeof m.feature === "string" && LIMIT_FEATURES.includes(m.feature))
    info.feature = m.feature as PlanLimitFeature;
  if (typeof m.plan === "string") info.plan = m.plan;
  if (typeof m.max === "number" && Number.isFinite(m.max)) info.max = m.max;
  if (typeof m.used === "number" && Number.isFinite(m.used)) info.used = m.used;

  return info;
}
